import React from 'react'
import { Dropdown, Button, Switch, Space, Divider } from 'antd'
import { GlobalOutlined } from '@ant-design/icons'
import { useLanguageStore } from '@/store/languageStore'
import { useTranslation } from '@/hooks/useTranslation'
import type { MenuProps } from 'antd'

interface LanguageSwitcherProps {
  compact?: boolean
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ compact = true }) => {
  const { language, setLanguage, debugMode, toggleDebugMode } = useLanguageStore()
  const { t } = useTranslation()

  const languageOptions: MenuProps['items'] = [
    {
      key: 'en',
      label: (
        <span>
          🇺🇸 {t('menu.language.english')}
          {language === 'en' ? ' ✓' : ''}
        </span>
      ),
      onClick: () => setLanguage('en'),
    },
    {
      key: 'vi',
      label: (
        <span>
          🇻🇳 {t('menu.language.vietnamese')}
          {language === 'vi' ? ' ✓' : ''}
        </span>
      ),
      onClick: () => setLanguage('vi'),
    },
  ]

  // Add debug mode toggle in development
  if (process.env.NODE_ENV === 'development') {
    languageOptions.push({
      key: 'divider',
      type: 'divider',
    })

    languageOptions.push({
      key: 'debug',
      label: (
        <Space>
          <span>{t('menu.language.debugMode')}</span>
          <Switch size="small" checked={debugMode} onClick={(checked) => checked !== debugMode && toggleDebugMode()} />
        </Space>
      ),
    })
  }

  if (compact) {
    return (
      <Dropdown menu={{ items: languageOptions }} trigger={['click']}>
        <Button
          type="text"
          icon={<GlobalOutlined />}
          style={{ fontSize: '16px' }}
          title={`${t('menu.language.select')} (${language.toUpperCase()})`}
        />
      </Dropdown>
    )
  }

  return (
    <div style={{ padding: '12px' }}>
      <div style={{ marginBottom: '12px' }}>
        <strong>{t('menu.language.select')}</strong>
      </div>
      <Space direction="vertical" style={{ width: '100%' }}>
        {languageOptions.map((item) => {
          if (item && typeof item === 'object' && 'key' in item) {
            if (item.type === 'divider') {
              return <Divider key={item.key} style={{ margin: '8px 0' }} />
            }
            return <div key={item.key}>{item.label}</div>
          }
          return null
        })}
      </Space>
    </div>
  )
}

export default LanguageSwitcher
